import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { notificationSocket } from '../lib/notificationSocket';
import type { NotificationSocketHandlers } from '../lib/notificationSocket';
import { NOTIFICATION_QUERY_KEY, NOTIFICATION_UNREAD_QUERY_KEY } from '../api/notificationApi';
import { REJECTED_ORDERS_QUERY_KEY } from '../api/orderApi';

/**
 * 把实时帧落到 React Query 缓存。
 *
 * 挂载点只有一个（登录后的顶栏通知下拉框），因此连接随登录状态自动建立与断开。
 * 原则是**服务端计数为准**：`unread`/`ready`/`syncDone` 帧带的是权威未读数，直接写进缓存；
 * 列表本身是分页投影，帧到达时整片失效让 React Query 重新取，不在前端手工插行。
 *
 * 服务端 `error` 帧只落 console 不弹提示：当前三类错误（握手重复、sinceId 非法、未鉴权）
 * 都是前端自身缺陷或重连中间态，用户既看不懂也无从处置，弹出来只会变成噪声。
 */
export function useNotificationSocket(enabled: boolean): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled) return;

    const handlers: NotificationSocketHandlers = {
      onReady: info => {
        queryClient.setQueryData(NOTIFICATION_UNREAD_QUERY_KEY, info.unreadCount);
      },
      onNotification: () => {
        queryClient.invalidateQueries({ queryKey: NOTIFICATION_QUERY_KEY });
        // 首页那条「被驳回订单」提示要跟着实时走：驳回是服务端在同一笔事务里给提交方
        // 发的 order_change 通知，只失效通知列表的话，提示行得等下次进首页才肯改口。
        queryClient.invalidateQueries({ queryKey: REJECTED_ORDERS_QUERY_KEY });
      },
      onUnread: count => {
        queryClient.setQueryData(NOTIFICATION_UNREAD_QUERY_KEY, count);
      },
      onSyncDone: payload => {
        queryClient.setQueryData(NOTIFICATION_UNREAD_QUERY_KEY, payload.unreadCount);
        // hasMore 表示增量超过单批上限；列表分页由失效重取覆盖，不必逐页追投
        if (payload.items.length > 0 || payload.hasMore) {
          queryClient.invalidateQueries({ queryKey: NOTIFICATION_QUERY_KEY });
        }
      },
      onError: message => {
        console.warn(`[通知实时通道] ${message}`);
      },
    };
    const release = notificationSocket.subscribe(handlers);

    // 断线时的指数退避最坏要等 30 秒，网络恢复与页面回到前台值得立刻重试一次
    const retryIfVisible = () => {
      if (document.visibilityState === 'visible') notificationSocket.resume();
    };
    window.addEventListener('online', retryIfVisible);
    document.addEventListener('visibilitychange', retryIfVisible);

    return () => {
      release();
      window.removeEventListener('online', retryIfVisible);
      document.removeEventListener('visibilitychange', retryIfVisible);
    };
  }, [enabled, queryClient]);
}
